import { useEffect, useState } from "react";
import React from "react";
import ModalForm from "./WebScrapingModal";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Box,
  CardContent,
  Card,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useWebScrapingAPI } from "@/hooks/useAdminDashboardService";
import { useTenantById } from "@/hooks/useTenantDashboardService";
import { useNavigate } from "react-router-dom";
import Loading from "@/components/Loading";

import type {
  CompanyProfileData,
  UpdateDataType,
} from "@/types/companyProfile";
import EditModal from "./EditModel";
import EditModalAbout from "./EditModalAbout";
import CompanyBasicDetails from "./CompanyBasicDetailsModal";
import { useUpdateActiveTenantsById, useUploadTenantLogoAPI } from "@/hooks/useAdminDashboardService";
import { useTenantContext } from "@/components/Navbar/TenantContext";
import { useDashboardTheme } from "../MeetingDashboard/themeHelper";

import { formatDateUS } from "@/utils/dateUtils";
import { toast } from "react-toastify";
import type { AxiosError } from "axios";
import { canEditCompanyProfile } from '@/config/permissions';
import useAuthStore from '@/store/useAuthStore';

const CompanyOverview = ({
  data,
  refetch,
}: {
  data: CompanyProfileData;
  refetch: () => void;
}) => {
  const {
    about,
    productsServices,
    valueProposition,
    competitors,
    uniqueSellingPoints,
    partners,
    targetIndustries,
    salesMethodology,
  } = data;

  const [expanded, setExpanded] = useState<string | false>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [updateNeedData, setUpdateNeedData] = useState<UpdateDataType>({
    title: "",
    titleMain: "",
    value: [],
    content: null,
  });
  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  const renderList = (items: string[]) => (
    <List dense>
      {items &&
        items?.length > 0 &&
        items.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemText primary={`• ${item}`} />
          </ListItem>
        ))}
    </List>
  );

  // const handleEdit = (
  //   data:
  //     | React.SetStateAction<UpdateDataType>
  //     | {
  //         title: string;
  //         content: string | undefined;
  //         value: string | undefined;
  //         titleMain: string;
  //       }
  //     | {
  //         title: string;
  //         content: JSX.Element;
  //         value: string[];
  //         titleMain: string;
  //       }
  // ) => {
  //   setIsModalOpen(true);
  //   if (
  //     typeof data === "object" &&
  //     "value" in data &&
  //     (!Array.isArray(data.value) || typeof data.value === "undefined")
  //   ) {
  //     setUpdateNeedData({
  //       ...data,
  //       value: data.value ? [data.value] : [],
  //     } as UpdateDataType);
  //   } else {
  //     setUpdateNeedData(data as UpdateDataType);
  //   }
  // };

  return (
    <>
      <Box
        sx={{
          backgroundColor: "#1C1A26",
          color: "#ffffff",
          p: 2,
          borderRadius: 2,
          mt: 2,
        }}
      >
        <Typography
          variant="h6"
          className="m-2"
          sx={{ fontWeight: "bold", m: 1 }}
        >
          Company Details
        </Typography>

        {[
          {
            title: "Who We Are",
            content: about,
            value: about,
            titleMain: "whoweare",
          },
          {
            title: "Value Proposition",
            content: renderList(valueProposition ?? []),
            value: valueProposition ?? [],
            titleMain: "valueProposition",
          },
          {
            title: "Sales Methodology",
            content: renderList(salesMethodology ?? []),
            value: salesMethodology ?? [],
            titleMain: "salesMethodology",
          },
          {
            title: "Product & Services",
            content: renderList(productsServices ?? []),
            value: productsServices ?? [],
            titleMain: "productsServices",
          },
          {
            title: "Differentiators",
            content: renderList(uniqueSellingPoints ?? []),
            value: uniqueSellingPoints ?? [],
            titleMain: "uniqueSellingPoints",
          },
          {
            title: "Competitors",
            content: renderList(competitors ?? []),
            value: competitors ?? [],
            titleMain: "competitors",
          },
          {
            title: "Partners",
            content: renderList(partners ?? []),
            value: partners ?? [],
            titleMain: "partners",
          },
          {
            title: "Targeted Industries",
            content: renderList(targetIndustries ?? []),
            value: targetIndustries ?? [],
            titleMain: "targetIndustries",
          },
        ].map((section, idx) => {
          const panelId = `panel-${idx}`;
          const isExpanded = expanded === panelId;

          return (
            <Accordion
              key={panelId}
              expanded={isExpanded}
              onChange={handleChange(panelId)}
              sx={{
                backgroundColor: "#29273A",
                color: "inherit",
                mb: 1.5,
                borderRadius: 2,
                boxShadow: "none",
                borderLeft: isExpanded
                  ? "4px solid #B8F890"
                  : "4px solid transparent",
                transition: "border-left 0.3s ease",
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                aria-controls={`${panelId}-content`}
                id={`${panelId}-header`}
              >
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 500,
                    color: isExpanded ? "#B8F890" : "#ffffff",
                    transition: "color 0.3s ease",
                    fontSize: "1.2rem",
                  }}
                >
                  {section.title}
                </Typography>
              </AccordionSummary>

              <AccordionDetails sx={{ pt: 0 }}>
                {typeof section.content === "string" ? (
                  <Typography sx={{ fontWeight: 400 }}>
                    {section.content}
                  </Typography>
                ) : (
                  section.content
                )}

                {/* {isExpanded && (
            <Box mt={2} display="flex" justifyContent="flex-end">
              <button
                className="px-4 py-1 bg-[#B8F890] text-black font-semibold rounded-full hover:bg-[#A4E67E] cursor-pointer"
                onClick={() => handleEdit(section)}
              >
                Edit
              </button>
            </Box>
          )} */}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </Box>

      {isModalOpen && updateNeedData?.titleMain == "whoweare" ? (
        <EditModalAbout
          updateNeedData={updateNeedData}
          setUpdateNeedData={setUpdateNeedData}
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setUpdateNeedData({
              title: "",
              titleMain: "",
              value: [],
              content: null,
            });
          }}
          onSave={() => {
            setIsModalOpen(false);
            setUpdateNeedData({
              title: "",
              titleMain: "",
              value: [],
              content: null,
            });
          }}
          data={data}
          refetch={refetch}
        />
      ) : (
        <EditModal
          open={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setUpdateNeedData({
              title: "",
              titleMain: "",
              value: [],
              content: null,
            });
          }}
          onSave={() => {
            setIsModalOpen(false);
            setUpdateNeedData({
              title: "",
              titleMain: "",
              value: [],
              content: null,
            });
          }}
          refetch={refetch}
          updateNeedData={updateNeedData}
          setUpdateNeedData={setUpdateNeedData}
          data={data}
          setInitallData={function (): void {
            throw new Error("Function not implemented.");
          }}
          initallData={{
            competitors: [],
            uniqueSellingPoints: [],
            targetIndustries: [],
            partners: [],
            salesMethodology: [],
            reviewText: "",
            whoweare: undefined,
            productsServices: [],
            valueProposition: [],
            tenantId: undefined,
          }}
        />
      )}
    </>
  );
};

const CompanyDetails = () => {
  const { role } = useAuthStore();
  const canEdit = canEditCompanyProfile(role as string);
  const { isDark } = useDashboardTheme();
  const [isWebScraping, setIsWebScraping] = useState(false);
  const [tenantProfile, setTenantProfile] = useState<CompanyProfileData | null>(
    null,
  );
  const tenantId = localStorage.getItem("tenantId") || "";
  const [editBaseDetails, setEditBaseDetails] = useState(false);
  const { mutate: updateTenants } = useUpdateActiveTenantsById();
  const { mutateAsync: uploadTenantLogo } = useUploadTenantLogoAPI();
  const { setTenantName } = useTenantContext();

  const { data, isPending, refetch } = useTenantById({
    tenantId,
    query: {
      include: "tenantProfile,usage_log,planMaster",
    },
  });
  const { mutate, isPending: loadingWebScrapiing } = useWebScrapingAPI();
  const id = localStorage.getItem("tenantId") || "";

  const [formData, setFormData] = useState({
    linkedin: data?.linkedIn_url || "",
    website: data?.website_url || "",
  });

  const [errors, setErrors] = useState({
    linkedin: "",
    website: "",
  });
  const navigate = useNavigate();

  useEffect(() => {
    if (data?.tenantProfile) {
      setTenantProfile(data.tenantProfile);
    }

    if (data?.website_url) {
      setFormData({ ...formData, website: data?.website_url });
    }
    if (data?.linkedIn_url) {
      setFormData((prev) => ({ ...prev, linkedin: data?.linkedIn_url }));
    }
  }, [data]);

  const validate = () => {
    const newErrors = { linkedin: "", website: "" };
    let isValid = true;

    const linkedin = formData.linkedin.trim();
    const website = formData.website.trim();

    const linkedinRegex =
      /^https:\/\/([a-z]{2,3}\.)?linkedin\.com\/(company|in)\/[a-zA-Z0-9_-]+\/?$/;

    const websiteRegex =
      /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/i;

    if (!linkedin) {
      newErrors.linkedin = "LinkedIn URL is required";
      isValid = false;
    } else if (!linkedinRegex.test(linkedin)) {
      newErrors.linkedin = "Enter a valid LinkedIn company or profile URL";
      isValid = false;
    }

    if (!website) {
      newErrors.website = "Website URL is required";
      isValid = false;
    } else if (!websiteRegex.test(website)) {
      newErrors.website = "Enter a valid website URL";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const [lastFetchedUrl, setLastFetchedUrl] = useState("");

  const handleWebScraping = () => {
    if (!id) return alert("Tenant ID not found!");
    const trimmedUrl = formData.website.trim().toLowerCase();
    if (lastFetchedUrl && lastFetchedUrl === trimmedUrl) {
      toast.error("Details for this URL have already been fetched.");
      return;
    }
    const payload = {
      id,
      linkedin: formData.linkedin,
      website: formData.website,
    };
    mutate(payload, {
      onSuccess: (data) => {
        setLastFetchedUrl(trimmedUrl);
        toast.success("Details fetched! Redirecting to company profile...");
        navigate("/company-profile", {
          state: data?.data,
        });
      },
      onError: (error: any) => {
        console.error("Web scraping failed:", error);
        if (error?.response?.status === 422) {
          toast.error("This company doesn't allow scraping data. Please fill manually.");
        } else {
          toast.error(error?.response?.data?.message || error?.message || "Error: Try again in 1 min, or fill manually.");
        }
      },
    });
  };

  if (isPending || loadingWebScrapiing) {
    return <Loading />;
  }
  return (
    <>
      <main className={"flex-1 px-8 my-4 " + (isDark ? "" : "light-theme-dashboard")}>
        {!canEdit && (
          <div className="bg-[#2A2640] border border-[#3D3760] rounded-lg px-4 py-2 mb-4 flex items-center gap-2">
            <span className="text-white/70 text-sm">🔒 View-only mode — contact your admin to make changes</span>
          </div>
        )}
        <Card
          sx={{
            width: "100%",
            backgroundColor: "#1C1A26",
            color: "#fff",
            mt: 12,
            p: 3,
            borderRadius: 3,
          }}
        >
          <CardContent>
            <Box display="flex" alignItems="center" gap={2}>
              <Box sx={{ maxWidth: "80%", display: "inline-block" }}>
                <Typography
                  variant="h6"
                  fontWeight="bold"
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {data?.name}
                </Typography>
              </Box>

              <Box
                px={2}
                py={0.5}
                borderRadius="999px"
                bgcolor="transparent"
                border={`1.5px solid ${data?.status === "active" ? "#B8F890" : "#FFBABA"}`}
                color={data?.status === "active" ? "#B8F890" : "#FFBABA"}
                fontWeight={600}
                fontSize={12}
              >
                {data?.status === "active" ? "Active" : "Inactive"}
              </Box>
              <Box
                sx={{
                  flex: "1 1 100%",
                  display: "flex",
                  justifyContent: "flex-end",
                }}
              >
                {canEdit && (
                  <button
                    className="px-4 py-0.5 bg-[#B8F890] text-black font-semibold rounded-full hover:bg-[#A4E67E]"
                    onClick={() => setEditBaseDetails(true)}
                  >
                    Edit
                  </button>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                mt: 3,
                rowGap: 3,
                columnGap: 4,
              }}
            >
              {[
                {
                  label: "Contact Email",
                  value: data?.contactEmail,
                },
                {
                  label: "Created At",
                  value: formatDateUS(data?.createdAt),
                },
                {
                  label: "Domain",
                  value: data?.domain,
                  isLink: true,
                },
                {
                  label: "Linkedin URL",
                  value: data?.linkedIn_url,
                  isLink: true,
                },
                {
                  label: "Type",
                  value: data?.type,
                },
                {
                  label: "Website URL",
                  value: data?.website_url,
                  isLink: true,
                },
              ].map((item, index) => (
                <Box key={index} sx={{ flex: "1 1 30%" }}>
                  <Typography sx={{ color: "#999", fontSize: 13 }}>
                    {item.label}
                  </Typography>
                  {item.isLink ? (
                    <Typography
                      component="a"
                      href={item.value || "#"}
                      target="_blank"
                      rel="noopener"
                      sx={{
                        fontWeight: 500,
                        textDecoration: "none",
                        color: "#fff",
                        wordBreak: "break-all",
                      }}
                    >
                      {item.value}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontWeight: 500 }}>
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </CardContent>
        </Card>

        <Box
          sx={{
            mt: 4,
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            gap: 3,
          }}
        >
          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 48%" } }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
              Automatic Scraping
            </Typography>

            <Box
              className="company-details-card"
              sx={{
                backgroundColor: "#1C1A26",
                borderRadius: 2,
                p: 3,
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <TextField
                  fullWidth
                  placeholder="Enter LinkedIn URL"
                  label="LinkedIn URL"
                  value={formData.linkedin}
                  onChange={(e) =>
                    setFormData({ ...formData, linkedin: e.target.value })
                  }
                  error={!!errors.linkedin}
                  helperText={errors.linkedin}
                  size="small"
                  sx={{
                    input: { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#aaa", fontSize: 13 },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#B8F890" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#B8F890" },
                      "&.Mui-focused fieldset": { borderColor: "#B8F890" },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#FFBABA",
                      fontSize: 12,
                      mt: 0.5,
                    },
                  }}
                />

                <TextField
                  fullWidth
                  placeholder="Enter Website URL"
                  label="Website URL"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                  error={!!errors.website}
                  helperText={errors.website}
                  size="small"
                  sx={{
                    input: { color: "#fff" },
                    "& .MuiInputLabel-root": { color: "#aaa", fontSize: 13 },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#B8F890" },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#555" },
                      "&:hover fieldset": { borderColor: "#B8F890" },
                      "&.Mui-focused fieldset": { borderColor: "#B8F890" },
                    },
                    "& .MuiFormHelperText-root": {
                      color: "#FFBABA",
                      fontSize: 12,
                      mt: 0.5,
                    },
                  }}
                />
              </Box>

              {canEdit && (
                <button
                  className="w-60 py-2 mt-4 bg-[#B8F890] text-black font-semibold rounded-full hover:bg-[#A4E67E]"
                  onClick={() => validate() && handleWebScraping()}
                >
                  Web Scraping
                </button>
              )}
            </Box>
          </Box>

          <Box sx={{ flex: { xs: "1 1 100%", md: "1 1 48%" } }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1.5}>
              Manual Entries
            </Typography>

            <Box
              className="company-details-card"
              sx={{
                backgroundColor: "#1C1A26",
                borderRadius: 2,
                p: 3,
                minHeight: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
              }}
            >
              <Typography sx={{ color: "#ccc", fontSize: 14, lineHeight: 1.6 }}>
                Manually input or upload company details for accurate data
                capture.
              </Typography>

              {canEdit && (
                <button
                  className="w-60 py-2 mt-5 bg-[#B8F890] text-black font-semibold rounded-full hover:bg-[#A4E67E]"
                  onClick={() => navigate("/company-profile-manual")}
                >
                  Web Scraping Entries
                </button>
              )}
            </Box>
          </Box>
        </Box>

        <Box
          className="company-details-card"
          sx={{
            mt: 4,
            backgroundColor: "#1C1A26",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography fontWeight="bold" mb={2}>
            Plan Details
          </Typography>
          <Typography>
            Plan:{" "}
            <strong>
              {data?.planMaster?.displayName} ({data?.planMaster?.name})
            </strong>
          </Typography>
          <Typography sx={{ my: 1 }}>
            Description: <strong>{data?.planMaster?.description}</strong>
          </Typography>
          <Typography>
            Price : <strong>${data?.planMaster?.price}</strong>
          </Typography>
        </Box>

        {tenantProfile && (
          <CompanyOverview data={tenantProfile} refetch={refetch} />
        )}
        <ModalForm open={isWebScraping} setOpen={setIsWebScraping} />

        <CompanyBasicDetails
          open={editBaseDetails}
          onClose={() => setEditBaseDetails(false)}
          onSave={async (modalData) => {
            const { logoFile, ...restData } = modalData;

            // Upload logo if provided
            if (logoFile) {
              const formData = new FormData();
              formData.append("files", logoFile);
              try {
                await uploadTenantLogo({ id, formData });
                toast.success("Organization logo updated successfully.");
              } catch (error) {
                toast.error("Failed to upload organization logo.");
                console.error(error);
              }
            }

            // Update other details
            updateTenants(
              {
                payload: {
                  ...restData,
                  status: restData.status === "active",
                  website_url: restData.website_url ?? "",
                },
                id,
              },
              {
                onSuccess: () => {
                  setTenantName(restData.name || "");
                  refetch();
                },
                onError: (error) => {
                  const axiosError = error as AxiosError<{ message: string }>;
                  const errorMessage =
                    axiosError.response?.data?.message ||
                    "Something went wrong";
                  toast.error(errorMessage);
                },
              },
            );
          }}
          defaultValues={{
            name: data?.name ?? "",
            contactEmail: data?.contactEmail ?? "",
            domain: data?.domain ?? "",
            linkedIn_url: data?.linkedIn_url ?? "",
            website_url: data?.website_url ?? null,
            type: data?.type ?? "",
            status: data?.status ?? "inactive",
          }}
        />
      </main>
    </>
  );
};

export default CompanyDetails;
